import { db } from '@util/firebase';
import {
  collection,
  CollectionReference,
  query,
  orderBy,
  getDocs,
  where,
  limit,
} from 'firebase/firestore';
import { NextApiRequest, NextApiResponse } from 'next';
import { BrandProp } from 'pages/brands';
import { z } from 'zod';
import * as Sentry from '@sentry/nextjs';
import { logApiError } from '@util/logApiError';
export const brandSchema = z.object({
  id: z.string(),
  old_id: z.string(),
  name: z.string(),
  letter: z.string(),
  image: z.string().optional(),
  description: z.string().default(''),
  url: z.string(),
  meta_title: z.string().optional(),
  meta_description: z.string().optional(),
  meta_keywords: z.string().optional(),
});
export type BrandObj = z.infer<typeof brandSchema>;
const brandResponseDataSchema = z
  .object({
    imageUrl: z.string(),
  })
  .catchall(brandSchema);
export const brandResponseSchema = z.object({
  data: brandResponseDataSchema,
});
export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  try {
    const brands = await getBrands();
    res.status(200).json({ brands });
  } catch (error) {
    await logApiError(error, req, 'fetch_brands');
    res.status(500).json({ error: 'Failed to fetch brands' });
  }
}

export async function getBrands(): Promise<BrandProp[]> {
  // Browser callers use the same-origin route; the server reads Firestore.
  if (typeof window !== 'undefined') {
    try {
      const res = await fetch('/api/brands');
      if (!res.ok) throw new Error(`brands BFF returned ${res.status}`);
      const body = (await res.json()) as { brands?: unknown };
      // Tolerant per-item parse: one malformed brand doc must not throw and
      // blank the entire list. See GB #1457 — three docs with array-typed
      // meta_keywords made `z.array(brandSchema).parse(...)` throw, the catch
      // returned [], and the required Brand input vanished platform-wide.
      return parseBrandsTolerant(body.brands);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { source: 'getBrandsFromBff' },
      });
      return [];
    }
  }

  try {
    const ref = collection(db, 'brands') as CollectionReference<BrandObj>;
    const q = query(ref, orderBy('name'));
    //letter ? query(ref, where('letter', '==', letter), orderBy('name'))

    const snap = await getDocs(q);
    const brands = snap.docs.map((d) => {
      const data = d.data();
      // Only return fields defined in brandSchema to avoid serialization issues
      // with Firestore Timestamps (createdAt, updatedAt) and undefined values
      return {
        id: data.id || d.id,
        old_id: data.old_id || '',
        name: data.name || '',
        letter: data.letter || '',
        image: data.image || '',
        description: data.description || '',
        url: data.url || '',
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
        meta_keywords: data.meta_keywords || '',
      };
    });
    return brands;
  } catch (error) {
    console.error("Error fetching brands:", error);
    return [];
  }
}

/**
 * Parse a raw brands payload one item at a time so a single malformed brand
 * document cannot throw and blank the entire list (GB #1457).
 *
 * Every well-formed brand is kept; each malformed one is dropped. A single
 * Sentry message is emitted per load (not one per bad doc): the message
 * string is a CONSTANT so Sentry's flood guard collapses repeats into one
 * issue, and the varying detail (how many were bad, first bad id, total
 * received) lives in `extra`. A payload that is 100% invalid still returns []
 * and still captures.
 */
export function parseBrandsTolerant(raw: unknown): BrandProp[] {
  if (!Array.isArray(raw)) {
    Sentry.captureMessage('getBrands: brands payload was not an array', {
      level: 'warning',
      tags: { source: 'getBrands' },
      extra: { receivedType: typeof raw },
    });
    return [];
  }

  const valid: BrandProp[] = [];
  let invalidCount = 0;
  let firstBadId: string | undefined;

  for (const item of raw) {
    const result = brandSchema.safeParse(item);
    if (result.success) {
      valid.push(result.data);
      continue;
    }
    invalidCount++;
    if (firstBadId === undefined) {
      // Best-effort id for triage; a malformed doc may lack a string `id`.
      const maybeId = (item as { id?: unknown } | null)?.id;
      firstBadId = typeof maybeId === 'string' ? maybeId : '(unknown)';
    }
  }

  if (invalidCount > 0) {
    Sentry.captureMessage('getBrands: dropped malformed brand doc(s)', {
      level: 'warning',
      tags: { source: 'getBrands' },
      extra: {
        invalidCount,
        totalReceived: raw.length,
        firstBadId,
      },
    });
  }

  return valid;
}

export async function getBrandBySlug(slug: string) {

  const ref = collection(db, 'brands') as CollectionReference<BrandObj>;
  const q = query(ref, where('url', '==', slug), limit(1));
  const snap = await getDocs(q);
  return snap.docs[0]?.data();
}
export async function getBrandByName(name: string) {
  // Product detail components call this in the browser. Resolve from the BFF
  // list so those callers no longer query Firestore directly.
  if (typeof window !== 'undefined') {
    const all = await getBrands();
    const lower = name.toLowerCase();
    return all.find((b) => b.name.toLowerCase() === lower) ?? null;
  }

  const ref = collection(db, 'brands') as CollectionReference<BrandObj>;
  const q = query(ref, where('name', '==', name), limit(1));
  const snap = await getDocs(q);
  return snap.docs[0]?.data() ?? null;
}
